import {
  Avatar,
  Badge,
  Button,
  Heading,
  Icon,
  TextField,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@elaniintc/goes-design-system";
import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { useOnClickOutside } from "usehooks-ts";
import { cn } from "@/utils/theme";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { NodeTypes, type NodeData } from "@/workflow_api/types/workflow";
import {
  useCloneNode,
  useDeleteNode,
  useDuplicateNode,
} from "@/workflow_api/services/nodes.api";
import clsx from "clsx";
import ScrollModal from "@/components/common/scroll-modal";
import type { IDropdownListItem } from "@/components/ui/dropdown-list/dropdown-list-item";
import { DropdownList } from "@/components/ui/dropdown-list/dropdown-list";
import { useGetFlows } from "@/queries/flows/flows.api";
import type { ApiFlowsResponseType } from "@/types/entities/flows";
import { Renderify } from "renderify-js";

interface Props {
  nodeId: string;
  flowId: string;
}

type FlowRecord = ApiFlowsResponseType["data"][number];

const canNodeBeTested = (node: NodeData): boolean => {
  const testableNodeTypes: string[] = [
    NodeTypes.IF,
    NodeTypes.SWITCH,
    NodeTypes.CALCULATED,
  ];
  if (node.inputs.length === 0) return false;
  if (testableNodeTypes.includes(node.type)) return true;
  return false;
};

const WorkflowNodeOptions = ({ nodeId, flowId }: Props) => {
  // ---- hooks SIEMPRE arriba, sin returns antes ----
  const { getNodeById, setDeletingNode } = useWorkflowStore();
  const {
    setNodeDetailsOpen,
    setSelectedNodeId,
    setTimerDetailsOpen,
    setModalState,
    isFlowBlocked,
  } = useUIStore();

  const [isDeleting, setIsDeleting] = useState(false);

  // refs y handlers de cierre
  const optionsRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(optionsRef as React.RefObject<HTMLDivElement>, () => {
    if (isDeleting) {
      setDeletingNode("");
      setIsDeleting(false);
    }
  });

  const { mutateAsync: deleteNodeMutation, isPending: isLoadingDelete } =
    useDeleteNode(flowId);
  const { mutateAsync: duplicateNodeMutation } = useDuplicateNode(flowId);
  const cloneNode = useCloneNode(flowId);

  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [cloneName, setCloneName] = useState("");
  const [flowSearch, setFlowSearch] = useState("");
  const [selectedFlow, setSelectedFlow] = useState<
    IDropdownListItem | undefined
  >(undefined);

  const {
    data: flowsData,
    isLoading: isLoadingFlows,
    isError: isErrorFlows,
  } = useGetFlows({
    page: 1,
    perPage: 9999,
    order_by: "name",
  });

  const flowToItem = useCallback(
    (f: FlowRecord): IDropdownListItem => {
      const idStr = String(f.id);
      const versionText = String(f.version ?? "");
      return {
        value: idStr,
        label: (
          <div className="flex items-center">
            <Avatar
              size="2xs"
              shape={null}
              className={clsx(
                "items-center justify-center border rounded-lg mr-2",
                f.parent_flow_id ? "border-info-quiet" : "border-action-quiet",
              )}
            >
              <div className="">
                <Renderify
                  condition={!f.parent_flow_id}
                  elseShow={
                    <Icon
                      type="fas"
                      name="users-rectangle"
                      size="sm"
                      className="text-info-quiet"
                    />
                  }
                >
                  <Icon
                    type="fas"
                    name="user-circle"
                    size="sm"
                    className="text-action-quiet"
                  />
                </Renderify>
              </div>
            </Avatar>
            <span className="text-body font-medium truncate">{f.name}</span>
            <pre className="font-normal text-body-quiet ml-2">
              v{versionText}.0
            </pre>
            {idStr === String(flowId) && (
              <Badge className="ml-auto" size="sm">
                Actual
              </Badge>
            )}
          </div>
        ),
      };
    },
    [flowId],
  );

  useEffect(() => {
    if (selectedFlow) return;
    const match = flowsData?.data?.find((f) => String(f.id) === String(flowId));
    if (match) setSelectedFlow(flowToItem(match));
  }, [flowsData, flowId, selectedFlow, flowToItem]);

  const filteredFlows: FlowRecord[] = useMemo(() => {
    const q = flowSearch.trim().toLowerCase();
    const list: FlowRecord[] = flowsData?.data ?? [];
    if (!q) return list;
    return list.filter((f) => {
      const name = (f.name ?? "").toLowerCase();
      const version = String(f.version ?? "").toLowerCase();
      return name.includes(q) || version.includes(q);
    });
  }, [flowsData, flowSearch]);

  const filteredFlowOptions: IDropdownListItem[] = useMemo(
    () => filteredFlows.map((f) => flowToItem(f)),
    [filteredFlows, flowToItem],
  );

  const node = getNodeById(nodeId);
  if (!node) return null;

  const handleRunNode = () => {
    setSelectedNodeId(nodeId);
    setModalState("test-input");
  };

  const handleDeleteAttempt = () => {
    setDeletingNode(nodeId);
    setIsDeleting(true);
  };

  const handleDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedNodeId(nodeId);
    setNodeDetailsOpen(true);
  };

  const handleTimerDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedNodeId(nodeId);
    setNodeDetailsOpen(true);
    setTimerDetailsOpen(true);
  };

  const canBeTested = canNodeBeTested(node.data as unknown as NodeData);
  const isNodeInitializer = node.data.type === NodeTypes.INITIALIZER;
  const isNodeEnd = node.data.type === NodeTypes.END;
  const isFormNode = node.data.type === NodeTypes.FORM;
  const canHaveTimer =
    node.data.type === NodeTypes.FORM ||
    node.data.type === NodeTypes.CORRECTION;

  const onDelete = async (): Promise<void> => {
    setIsDeleting(false);
    setDeletingNode("");

    try {
      await deleteNodeMutation({ node_id: nodeId });
      if (!isMountedRef.current) return;
    } catch (error) {
      console.error("Error al eliminar:", error);
      if (isMountedRef.current) {
        setIsDeleting(true);
        setDeletingNode(nodeId);
      }
    }
  };

  const handleCloneClick = async (): Promise<void> => {
    if (isFormNode) {
      setCloneModalOpen(true);
      return;
    }

    try {
      await duplicateNodeMutation({
        node_id: nodeId,
        flow_id: flowId,
      });
    } catch (error) {
      console.error("Error al duplicar nodo:", error);
    }
  };

  const handleConfirmClone = async (): Promise<void> => {
    if (!selectedFlow?.value || !cloneName) return;
    try {
      await cloneNode.mutateAsync({
        nodeId,
        body: {
          target_flow_id: String(selectedFlow.value),
          name: cloneName,
        },
      });

      setCloneName("");
      setSelectedFlow(undefined);
      setCloneModalOpen(false);
      setFlowSearch("");
    } catch (error) {
      console.error("Error al clonar:", error);
    }
  };

  if (isNodeInitializer) return null;

  // ---- render ----
  if (isDeleting) {
    return (
      <OptionTooltip
        title="¿Seguro que deseas eliminar?"
        defaultOpen={true}
        onChange={() => { }}
        danger
      >
        <div
          className="transition-opacity z-30 rounded-lg absolute -top-12 inset-x-0 flex w-fit items-center justify-center shadow-danger-md bg-neutral-object mx-auto"
          ref={optionsRef}
        >
          <Button
            type="button"
            variant="quiet"
            size="icon"
            className="size-9 no-drag"
            aria-label="Cancelar eliminación"
            group="left"
            disabled={isLoadingDelete}
            onClick={() => {
              setDeletingNode("");
              setIsDeleting(false);
            }}
          >
            <Icon type="far" name="times" />
          </Button>
          <Button
            type="button"
            variant="quiet"
            size="icon"
            className="size-9 no-drag"
            aria-label="Eliminar nodo"
            group="right"
            disabled={isLoadingDelete}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Icon type="far" name="trash" className="text-danger" />
          </Button>
        </div>
      </OptionTooltip>
    );
  }

  if (isNodeEnd) {
    // Si el flujo está bloqueado, no mostrar opciones para nodos END
    if (isFlowBlocked) return null;

    return (
      <div>
        <div
          className={clsx(
            "opacity-0 group-hover:opacity-100 hover:opacity-100",
            "transition-opacity z-[42] w-max rounded-lg absolute -top-12 left-1/2 -translate-x-1/2 flex items-center shadow-neutral-md bg-neutral-object",
          )}
        >
          <OptionTooltip title="Eliminar">
            <Button
              type="button"
              variant="quiet"
              size="icon"
              className="size-9 no-drag"
              aria-label="Eliminar nodo"
              onClick={handleDeleteAttempt}
            >
              <Icon type="far" name="trash" />
            </Button>
          </OptionTooltip>
        </div>
      </div>
    );
  }

  // Si el flujo está bloqueado, solo mostrar el botón de Detalles
  if (isFlowBlocked) {
    return (
      <div>
        <div
          className={clsx(
            "opacity-0 group-hover:opacity-100 hover:opacity-100",
            "transition-opacity z-[42] w-max rounded-lg absolute -top-12 left-1/2 -translate-x-1/2 flex items-center shadow-neutral-md bg-neutral-object",
          )}
        >
          <OptionTooltip title="Detalles">
            <Button
              type="button"
              variant="quiet"
              size="icon"
              className="size-9 no-drag"
              aria-label="Detalles de nodo"
              onClick={handleDetails}
            >
              <Icon type="far" name="ellipsis-vertical" />
            </Button>
          </OptionTooltip>
        </div>
      </div>
    );
  }

  return (
    <div>
      {isFormNode && (
        <ScrollModal
          isOpen={cloneModalOpen}
          onClose={() => {
            setCloneModalOpen(false);
          }}
        >
          <div className="text-center p-8">
            <Heading variant="h2" className="!mb-0">
              Clonar objeto
            </Heading>
            <p className="text-center text-body font-normal leading-3xl pt-2">
              Define el nuevo nombre para el nuevo objeto y en qué flujo debe
              ser colocado.
            </p>
            <section className="text-left pt-8 space-y-4">
              <TextField
                label="Nombre del objeto"
                required
                placeholder="Escribe el nombre del objeto"
                value={cloneName}
                onChange={(e) => setCloneName(e.target.value)}
              />
              <div className="space-y-2">
                <p className="text-body text-sm">Flujo destino</p>
                {isLoadingFlows ? (
                  <div className="text-body-quiet py-2">Cargando flujos...</div>
                ) : isErrorFlows ? (
                  <div className="text-danger py-2">Error al cargar flujos</div>
                ) : (
                  <div className="flex">
                    <DropdownList
                      options={filteredFlowOptions}
                      placeholder="Selecciona un flujo destino"
                      widthClassName="flex-1"
                      search={{
                        placeholder: "Buscar flujo...",
                        query: flowSearch,
                        onSearch: setFlowSearch,
                      }}
                      selectedValue={selectedFlow}
                      toggleSelection={(item) => setSelectedFlow(item)}
                    />
                  </div>
                )}
              </div>
            </section>
            <div className="w-full grid grid-cols-1 tablet:grid-cols-2 pt-8 gap-3">
              <Button
                variant="tertiary"
                size="sm"
                type="button"
                className="min-w-[120px] cursor-pointer"
                onClick={() => setCloneModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="default"
                size="sm"
                type="button"
                onClick={handleConfirmClone}
                disabled={
                  !cloneName || !selectedFlow?.value || cloneNode.isPending
                }
                className="min-w-[140px] cursor-pointer disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-none disabled:shadow-none"
              >
                Aceptar
              </Button>
            </div>
          </div>
        </ScrollModal>
      )}

      {/* ------ Options ------- */}
      <div
        className={clsx(
          "opacity-0 group-hover:opacity-100 hover:opacity-100",
          "transition-opacity z-[42] w-max rounded-lg absolute -top-12 left-1/2 -translate-x-1/2 flex items-center shadow-neutral-md bg-neutral-object",
        )}
      >
        {canBeTested && (
          <OptionTooltip title="Probar">
            <Button
              type="button"
              variant="quiet"
              size="icon"
              className="size-9 no-drag"
              aria-label="Probar nodo"
              group="left"
              onClick={handleRunNode}
            >
              <Icon type="far" name="play" />
            </Button>
          </OptionTooltip>
        )}
        {/* {isNodeInitializer && (
          <OptionTooltip title="Probar">
            <Button
              type="button"
              variant="quiet"
              size="icon"
              className="size-9 no-drag"
              aria-label="Probar nodo"
              group="left"
              onClick={handleRunNode}
            >
              <Icon type="far" name="play" />
            </Button>
          </OptionTooltip>
        )} */}
        {canHaveTimer && (
          <OptionTooltip title="Temporizador">
            <Button
              type="button"
              variant="quiet"
              size="icon"
              className="size-9 no-drag"
              aria-label="Configurar temporizador"
              group="left"
              onClick={handleTimerDetails}
            >
              <Icon type="far" name="alarm-clock" />
            </Button>
          </OptionTooltip>
        )}
        {
          <OptionTooltip title="Clonar">
            <Button
              type="button"
              variant="quiet"
              size="icon"
              className="size-9 no-drag"
              aria-label="Clonar nodo"
              group={
                isNodeInitializer || canHaveTimer || canBeTested
                  ? "middle"
                  : "left"
              }
              onClick={async () => {
                await handleCloneClick();
              }}
            >
              <Icon type="far" name="copy" />
            </Button>
          </OptionTooltip>
        }
        {!isNodeInitializer && (
          <OptionTooltip title="Eliminar">
            <Button
              type="button"
              variant="quiet"
              size="icon"
              className="size-9 no-drag"
              aria-label="Eliminar nodo"
              group="middle"
              onClick={handleDeleteAttempt}
            >
              <Icon type="far" name="trash" />
            </Button>
          </OptionTooltip>
        )}
        <OptionTooltip title="Detalles">
          <Button
            type="button"
            variant="quiet"
            size="icon"
            className="size-9 no-drag"
            aria-label="Detalles de nodo"
            group="right"
            onClick={handleDetails}
          >
            <Icon type="far" name="ellipsis-vertical" />
          </Button>
        </OptionTooltip>
      </div>
    </div>
  );
};

export default WorkflowNodeOptions;

const OptionTooltip = ({
  children,
  title,
  defaultOpen = false,
  onChange,
  danger = false,
}: {
  children: React.ReactNode;
  title: string;
  defaultOpen?: boolean;
  onChange?: (open: boolean) => void;
  danger?: boolean;
}) => {
  return (
    <Tooltip
      defaultOpen={defaultOpen}
      delayDuration={0}
      {...(onChange ? { onOpenChange: onChange } : {})}
    >
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        className={cn(danger ? "shadow-danger-md" : "")}
      >
        {title}
      </TooltipContent>
    </Tooltip>
  );
};
